import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import iconLoadingIA from "../imagens/ialoading.gif";
import RemoveBackgroundModal from "./RemoveBackgroundModal";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.07 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 260, damping: 22 } },
};

const tips = [
  { icon: "fa-circle-check", text: "Prefira imagens PNG com fundo transparente" },
  { icon: "fa-expand", text: "Resolução recomendada: 500×500 px ou maior" },
  { icon: "fa-wand-magic-sparkles", text: "Use a IA para remover o fundo automaticamente" },
];

function PersonalizarLogo({ video, setVisivel, onUpdate, user, creditos, setCreditos }) {
  const fileUploadRef = useRef(null);
  const [logoAtual, setLogoAtual] = useState(video.logo);
  const [imagemParaProcessar, setImagemParaProcessar] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingIA, setLoadingIA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const abrirUpload = () => fileUploadRef.current.click();

  const handleFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setImagemParaProcessar(file);
    setShowModal(true);
  };

  const handleUpload = (e) => handleFile(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const processarImagemConfirm = async (resultado) => {
    if (!resultado) return;
    setLoading(true);
    try {
      let urlFinal = null;
      if (typeof resultado === 'string') {
        if (resultado.startsWith('data:')) {
          // Sobe dataURL para gerar URL permanente
          const blob = await (await fetch(resultado)).blob();
          const fd = new FormData();
          fd.append('imagem', blob, 'logo.png');
          const up = await fetch('https://videooferta.com/api/uploadImagem.php', {
            method: 'POST', body: fd,
          });
          const upJ = await up.json();
          if (upJ?.url) urlFinal = upJ.url;
        } else {
          urlFinal = resultado;
        }
      } else {
        // File: upload normal
        const fd = new FormData();
        fd.append('imagem', resultado);
        const up = await fetch('https://videooferta.com/api/uploadImagem.php', {
          method: 'POST', body: fd,
        });
        const upJ = await up.json();
        if (upJ?.url) urlFinal = upJ.url;
      }
      if (!urlFinal) throw new Error('URL não retornada');
      await salvarLogoVideo(urlFinal);
    } catch (err) {
      console.error(err);
      alert('Erro ao salvar logo');
    } finally {
      setLoading(false);
      setShowModal(false);
      setImagemParaProcessar(null);
    }
  };

  async function salvarLogoVideo(urlImagem) {
    const res = await fetch("https://videooferta.com/api/salvar-logo.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id_video: video.video_id, imagem: urlImagem }),
    });
    const data = await res.json();
    if (data.success) {
      setLogoAtual(data.logo);
      onUpdate();
    } else {
      alert("Erro ao salvar logo");
    }
  }

  return (
    <div className="col2 prod-panel">
      {/* ── Header ── */}
      <div className="prod-header">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="prod-header__icon-wrap">
            <i className="fa-solid fa-sliders" />
          </span>
          <h2 className="prod-header__title">Personalizar</h2>
        </div>
      </div>

      {/* ── Body ── */}
      <motion.div
        className="prod-body"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ gap: 20 }}
      >
        {/* Current logo preview */}
        <motion.div variants={itemVariants} className="pers-preview">
          <AnimatePresence mode="wait">
            {logoAtual ? (
              <motion.div
                key="logo"
                className="pers-preview__card"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                onClick={abrirUpload}
              >
                <img src={logoAtual} alt="Logo atual" className="pers-preview__img" />
                <div className="pers-preview__overlay">
                  <i className="fa-solid fa-camera" />
                  <span>Trocar logo</span>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                className={`pers-dropzone${dragOver ? " pers-dropzone--active" : ""}`}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                onClick={abrirUpload}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
              >
                <div className="pers-dropzone__icon">
                  <i className="fa-solid fa-cloud-arrow-up" />
                </div>
                <p className="pers-dropzone__title">Enviar logo</p>
                <p className="pers-dropzone__hint">
                  Arraste uma imagem ou clique para selecionar
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          <input
            type="file"
            ref={fileUploadRef}
            style={{ display: "none" }}
            accept="image/*"
            onChange={handleUpload}
          />
        </motion.div>

        {/* Action buttons when logo exists */}
        {logoAtual && (
          <motion.div variants={itemVariants} className="pers-actions">
            <button className="pers-action-btn" onClick={abrirUpload}>
              <i className="fa-solid fa-arrow-up-from-bracket" />
              Enviar nova logo
            </button>
            <button
              className="pers-action-btn pers-action-btn--danger"
              onClick={async () => {
                await salvarLogoVideo("");
                setLogoAtual("");
              }}
            >
              <i className="fa-solid fa-trash-can" />
              Remover logo
            </button>
          </motion.div>
        )}

        {/* Tips section */}
        <motion.div variants={itemVariants} className="pers-tips">
          <div className="pers-tips__header">
            <i className="fa-solid fa-lightbulb" />
            <span>Dicas para uma boa logo</span>
          </div>
          <ul className="pers-tips__list">
            {tips.map((tip) => (
              <li key={tip.text} className="pers-tips__item">
                <i className={`fa-solid ${tip.icon}`} />
                <span>{tip.text}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Redes Sociais e Endereço */}
        <motion.div variants={itemVariants} style={{ marginTop: 8 }}>
          <RedesSociaisCard key={video?.video_id || video?.id || 'sem-video'} video={video} onUpdate={onUpdate} />
        </motion.div>

        {/* Exibição das redes sociais no vídeo */}
        <motion.div variants={itemVariants} style={{ marginTop: 8 }}>
          <ExibicaoRedesCard video={video} onUpdate={onUpdate} />
        </motion.div>
      </motion.div>

      {/* ── Modal Remover Fundo ── */}
      <RemoveBackgroundModal
        open={showModal}
        onClose={() => { setShowModal(false); setImagemParaProcessar(null); }}
        source={imagemParaProcessar}
        userId={user?.id}
        creditos={creditos}
        onCreditosUpdate={(novo) => setCreditos && setCreditos(novo)}
        onConfirm={processarImagemConfirm}
      />
    </div>
  );
}

export default PersonalizarLogo;

// =============================================================
//  RedesSociaisCard — seção dentro do menu Personalizar
//  Permite configurar redes sociais + endereço com cores e bordas.
//  Salva em /api/atualizar-video.php (campos redes_enabled +
//  redes_sociais como JSON, com chave especial _config).
// =============================================================

const REDES_CAMPOS = [
  { key: "instagram", icon: "fa-brands fa-instagram", iconBg: "linear-gradient(135deg,#feda75,#fa7e1e 40%,#d62976 70%,#962fbf 100%)", placeholder: "@seu_instagram" },
  { key: "whatsapp",  icon: "fa-brands fa-whatsapp",  iconBg: "#25D366", placeholder: "(00) 00000-0000" },
  { key: "facebook",  icon: "fa-brands fa-facebook",  iconBg: "#1877F2", placeholder: "Sua página" },
  { key: "site",      icon: "fa-solid fa-globe",      iconBg: "#3b82f6", placeholder: "www.seusite.com" },
  { key: "endereco",  icon: "fa-solid fa-location-dot", iconBg: "#dc2626", placeholder: "Seu endereço" },
];

const CORES_FORMA = ["#ef4444", "#73207C", "#3b82f6"];
const CORES_FONTE = ["#000000", "#ffffff"];

function mascaraTelefone(v) {
  const d = String(v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function parseRedesSociais(video) {
  if (!video?.redes_sociais) return {};
  try {
    if (typeof video.redes_sociais === "string") return JSON.parse(video.redes_sociais) || {};
    return video.redes_sociais || {};
  } catch {
    return {};
  }
}

function RedesSociaisCard({ video, onUpdate }) {
  const dadosVideo = useMemo(() => parseRedesSociais(video), [video]);
  const videoId = video?.video_id || video?.id || null;
  const usuarioId = video?.usuario_id || null;

  // Assinaturas para detectar mudanças REAIS vindas do servidor.
  const sigConfig = useMemo(() => JSON.stringify(dadosVideo?._config || {}), [dadosVideo]);
  const sigUsuario = useMemo(
    () =>
      REDES_CAMPOS.map(({ key }) => `${key}=${video?.[`usuario_${key}`] || ''}`).join('|') +
      `|nome_estabelecimento=${video?.usuario_nome_estabelecimento || ''}`,
    [video]
  );

  const [aberto, setAberto] = useState(true);
  const [bordasArredondadas, setBordasArredondadas] = useState(true);
  const [corForma, setCorForma] = useState(CORES_FORMA[0]);
  const [corFonte, setCorFonte] = useState(CORES_FONTE[1]);
  // Nome do estabelecimento (perfil do usuário)
  const [nomeEstabelecimento, setNomeEstabelecimento] = useState("");
  // valores = perfil do usuário (instagram, whatsapp, ...)
  const [valores, setValores] = useState({});
  // habilitados = config do vídeo (quais redes aparecem neste vídeo)
  const [habilitados, setHabilitados] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState(null);
  const dirtyRef = useRef(false);
  const lastSigConfigRef = useRef(null);
  const lastSigUsuarioRef = useRef(null);

  // Carrega valores SÓ quando a assinatura vinda do servidor muda,
  // evitando sobrescrita após o save (quando o video ainda não recarregou).
  useEffect(() => {
    if (!videoId) return;
    if (dirtyRef.current) return;
    if (lastSigUsuarioRef.current === sigUsuario && lastSigConfigRef.current === sigConfig) return;

    const v = {};
    REDES_CAMPOS.forEach(({ key }) => {
      v[key] = video?.[`usuario_${key}`] || "";
    });
    setValores(v);
    setNomeEstabelecimento(video?.usuario_nome_estabelecimento || "");

    const cfg = dadosVideo._config || {};
    const habCfg = cfg.habilitados || {};
    setBordasArredondadas(cfg.bordasArredondadas !== false);
    setCorForma(cfg.corForma || CORES_FORMA[0]);
    setCorFonte(cfg.corFonte || CORES_FONTE[1]);
    const h = {};
    REDES_CAMPOS.forEach(({ key }) => {
      h[key] = habCfg[key] !== undefined ? !!habCfg[key] : false;
    });
    setHabilitados(h);

    lastSigUsuarioRef.current = sigUsuario;
    lastSigConfigRef.current = sigConfig;
  }, [videoId, sigUsuario, sigConfig, video, dadosVideo]);

  function atualizarValor(key, valor) {
    dirtyRef.current = true;
    let v = valor;
    if (key === "whatsapp") v = mascaraTelefone(valor);
    setValores((p) => ({ ...p, [key]: v }));
  }

  async function salvar() {
    if (!videoId) return;
    setSalvando(true);
    setMsg(null);

    const cfgVideo = {
      ...(dadosVideo._config || {}),
      bordasArredondadas,
      corForma,
      corFonte,
      habilitados: REDES_CAMPOS.reduce((acc, { key }) => ({ ...acc, [key]: !!habilitados[key] }), {}),
    };

    // O vídeo NÃO armazena os valores das redes — só a config.
    // Os valores ficam no perfil do usuário.
    const redesPayload = { _config: cfgVideo };

    try {
      // 1) salva perfil do usuário (se houver id) — apenas campos preenchidos
      if (usuarioId) {
        const corpoUsuario = { id: usuarioId };
        let temAlgo = false;
        REDES_CAMPOS.forEach(({ key }) => {
          const val = (valores[key] || "").trim();
          if (val !== "") { corpoUsuario[key] = val; temAlgo = true; }
        });
        // Sempre envia nome_estabelecimento (mesmo vazio, para permitir limpar)
        corpoUsuario.nome_estabelecimento = (nomeEstabelecimento || "").trim();
        temAlgo = true;
        if (temAlgo) {
          await fetch("https://videooferta.com/api/atualizar-usuario.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(corpoUsuario),
          });
        }
      }

      // 2) salva config no vídeo
      const res = await fetch("https://videooferta.com/api/atualizar-video.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: videoId,
          redes_sociais: JSON.stringify(redesPayload),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success || res.ok) {
        setMsg({ tipo: "ok", txt: "Alterações salvas!" });
        dirtyRef.current = false;
        onUpdate && onUpdate();
      } else {
        setMsg({ tipo: "err", txt: data?.error || "Erro ao salvar." });
      }
    } catch {
      setMsg({ tipo: "err", txt: "Erro de conexão." });
    } finally {
      setSalvando(false);
      setTimeout(() => setMsg(null), 3000);
    }
  }

  return (
    <div style={{
      background: "#fff",
      border: "1px solid #E8E1EF",
      borderRadius: 16,
      padding: 20,
      boxShadow: "0 4px 18px rgba(115,32,124,0.06)",
    }}>
      {/* Header */}
      <button
        type="button"
        onClick={() => setAberto((p) => !p)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 10,
          background: "transparent", border: 0, padding: 0, cursor: "pointer",
        }}
      >
        <span style={{
          width: 32, height: 32, borderRadius: 10, display: "inline-flex",
          alignItems: "center", justifyContent: "center",
          background: "#F5EDFA", color: "#73207C",
        }}>
          <i className="fa-solid fa-share-nodes" />
        </span>
        <h3 style={{ margin: 0, flex: 1, textAlign: "left", fontSize: 15, fontWeight: 700, color: "#0F0A15" }}>
          Redes Sociais e Endereço
        </h3>
        <i className={`fa-solid ${aberto ? "fa-chevron-up" : "fa-chevron-down"}`} style={{ color: "#73207C" }} />
      </button>

      <AnimatePresence initial={false}>
        {aberto && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22 }}
            style={{ overflow: "hidden" }}
          >
            <div style={{ paddingTop: 20, display: "flex", flexDirection: "column", gap: 20 }}>
              {/* Nome do estabelecimento */}
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 800, color: "#73207C", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
                  <i className="fa-solid fa-store" />
                  <span>Nome do estabelecimento</span>
                </div>
                <input
                  type="text"
                  value={nomeEstabelecimento}
                  onChange={(e) => { dirtyRef.current = true; setNomeEstabelecimento(e.target.value); }}
                  placeholder="Ex.: Mercado do João"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "2px solid #73207C",
                    fontSize: 13,
                    outline: "none",
                    background: "#fff",
                    color: "#0F0A15",
                    transition: "box-shadow .15s, border-color .15s",
                  }}
                  onFocus={(e) => { e.target.style.borderColor = "#3C005A"; e.target.style.boxShadow = "0 0 0 3px #F5EDFA"; }}
                  onBlur={(e) => { e.target.style.borderColor = "#73207C"; e.target.style.boxShadow = "none"; }}
                />
                <div style={{ fontSize: 11, color: "#5C4A63", marginTop: 6 }}>
                  Será adicionado ao final da introdução e do fechamento na locução.
                </div>
              </div>

              {/* Bordas arredondadas */}
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 14, color: "#0F0A15" }}>
                <input
                  type="checkbox"
                  checked={bordasArredondadas}
                  onChange={(e) => { dirtyRef.current = true; setBordasArredondadas(e.target.checked); }}
                  style={{ accentColor: "#73207C", width: 16, height: 16 }}
                />
                Bordas arredondadas
              </label>

              {/* Cores */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <SeletorCor
                  label="Cor da forma"
                  iconLabel="fa-solid fa-shapes"
                  cores={CORES_FORMA}
                  valor={corForma}
                  onChange={(v) => { dirtyRef.current = true; setCorForma(v); }}
                />
                <SeletorCor
                  label="Cor da fonte"
                  iconLabel="fa-solid fa-font"
                  cores={CORES_FONTE}
                  valor={corFonte}
                  onChange={(v) => { dirtyRef.current = true; setCorFonte(v); }}
                />
              </div>

              {/* Campos */}
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 4 }}>
                {REDES_CAMPOS.map(({ key, icon, iconBg, placeholder }) => (
                  <div key={key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <input
                      type="checkbox"
                      checked={!!habilitados[key]}
                      onChange={(e) => { dirtyRef.current = true; setHabilitados((p) => ({ ...p, [key]: e.target.checked })); }}
                      style={{ accentColor: "#73207C", width: 16, height: 16, flexShrink: 0 }}
                    />
                    <span style={{
                      width: 36, height: 36, borderRadius: "50%",
                      background: iconBg,
                      color: "#fff",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      fontSize: 16, flexShrink: 0,
                    }}>
                      <i className={icon} />
                    </span>
                    <input
                      type="text"
                      value={valores[key] || ""}
                      onChange={(e) => atualizarValor(key, e.target.value)}
                      placeholder={placeholder}
                      style={{
                        flex: 1,
                        padding: "10px 12px",
                        borderRadius: 10,
                        border: "2px solid #73207C",
                        fontSize: 13,
                        outline: "none",
                        background: "#fff",
                        color: "#0F0A15",
                        transition: "box-shadow .15s, border-color .15s",
                      }}
                      onFocus={(e) => { e.target.style.borderColor = "#3C005A"; e.target.style.boxShadow = "0 0 0 3px #F5EDFA"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#73207C"; e.target.style.boxShadow = "none"; }}
                    />
                  </div>
                ))}
              </div>

              {/* Mensagem */}
              {msg && (
                <div style={{
                  fontSize: 13, padding: "8px 12px", borderRadius: 8,
                  background: msg.tipo === "ok" ? "#dcfce7" : "#fee2e2",
                  color: msg.tipo === "ok" ? "#166534" : "#991b1b",
                }}>
                  {msg.txt}
                </div>
              )}

              {/* Botão salvar */}
              <button
                type="button"
                onClick={salvar}
                disabled={salvando}
                style={{
                  marginTop: 6,
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: 12,
                  border: 0,
                  cursor: salvando ? "wait" : "pointer",
                  background: "linear-gradient(135deg,#8b3aa1 0%,#73207C 100%)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  boxShadow: "0 6px 18px rgba(115,32,124,0.35)",
                  opacity: salvando ? 0.7 : 1,
                }}
              >
                <i className={`fa-solid ${salvando ? "fa-spinner fa-spin" : "fa-check"}`} />
                {salvando ? "Salvando..." : "Salvar Alterações"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SeletorCor({ label, iconLabel, cores, valor, onChange }) {
  const inputRef = useRef(null);
  const isPersonalizada = !cores.includes(valor);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 800, color: "#73207C", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
        <i className={iconLabel} />
        <span>{label}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          title="Cor personalizada"
          style={{
            width: 26, height: 26, borderRadius: "50%",
            background: isPersonalizada
              ? valor
              : "conic-gradient(from 0deg, #ef4444, #f59e0b, #84cc16, #06b6d4, #3b82f6, #a855f7, #ef4444)",
            border: isPersonalizada ? "2px solid #73207C" : "2px solid #fff",
            boxShadow: "0 0 0 1px #E8E1EF",
            cursor: "pointer",
            position: "relative",
          }}
        >
          <i className="fa-solid fa-eye-dropper" style={{ color: "#fff", fontSize: 10, textShadow: "0 0 2px rgba(0,0,0,0.6)" }} />
        </button>
        <input
          ref={inputRef}
          type="color"
          value={valor}
          onChange={(e) => onChange(e.target.value)}
          style={{ display: "none" }}
        />
        {cores.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            title={c}
            style={{
              width: 26, height: 26, borderRadius: "50%",
              background: c,
              border: valor === c ? "2px solid #73207C" : "2px solid #fff",
              boxShadow: valor === c ? "0 0 0 1px #73207C" : "0 0 0 1px #E8E1EF",
              cursor: "pointer",
            }}
          />
        ))}
      </div>
    </div>
  );
}

// =============================================================
//  ExibicaoRedesCard — controla SE/ONDE as redes sociais aparecem
//  no vídeo (rodapé durante produtos / no fechamento).
//  Salva em /api/atualizar-video.php (redes_enabled + redes_outro).
// =============================================================
function ExibicaoRedesCard({ video, onUpdate }) {
  const videoId = video?.video_id || video?.id || null;

  const boolFromAny = (v) => v === 1 || v === '1' || v === true;

  const [aberto, setAberto] = useState(true);
  const [exibirRodape, setExibirRodape] = useState(boolFromAny(video?.redes_enabled));
  const [exibirOutro, setExibirOutro] = useState(boolFromAny(video?.redes_outro));
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState(null);

  // Sincroniza quando o vídeo recarrega
  useEffect(() => {
    setExibirRodape(boolFromAny(video?.redes_enabled));
    setExibirOutro(boolFromAny(video?.redes_outro));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, video?.redes_enabled, video?.redes_outro]);

  async function salvarExibicao(novoRodape, novoOutro) {
    if (!videoId) return;
    setSalvando(true);
    setMsg(null);
    try {
      const res = await fetch("https://videooferta.com/api/atualizar-video.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: videoId,
          redes_enabled: novoRodape ? 1 : 0,
          redes_outro: novoOutro ? 1 : 0,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success || res.ok) {
        setMsg({ tipo: "ok", txt: "Salvo!" });
        onUpdate && onUpdate();
      } else {
        setMsg({ tipo: "err", txt: data?.error || "Erro ao salvar." });
      }
    } catch {
      setMsg({ tipo: "err", txt: "Erro de conexão." });
    } finally {
      setSalvando(false);
      setTimeout(() => setMsg(null), 2500);
    }
  }

  function toggleRodape(v) {
    setExibirRodape(v);
    salvarExibicao(v, exibirOutro);
  }
  function toggleOutro(v) {
    setExibirOutro(v);
    salvarExibicao(exibirRodape, v);
  }

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 12,
    cursor: "pointer",
    fontSize: 14,
    color: "#0F0A15",
    border: "1px solid #E8E1EF",
    background: "#fff",
    transition: "all 0.2s",
  };

  return (
    <div style={{
      background: "#fff",
      border: "1px solid #E8E1EF",
      borderRadius: 16,
      padding: 20,
      boxShadow: "0 4px 18px rgba(115,32,124,0.06)",
    }}>
      <button
        type="button"
        onClick={() => setAberto((p) => !p)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 10,
          background: "transparent", border: 0, padding: 0, cursor: "pointer",
        }}
      >
        <span style={{
          width: 32, height: 32, borderRadius: 10, display: "inline-flex",
          alignItems: "center", justifyContent: "center",
          background: "#F5EDFA", color: "#73207C",
        }}>
          <i className="fa-solid fa-eye" />
        </span>
        <span style={{ fontWeight: 700, color: "#0F0A15", flex: 1, textAlign: "left" }}>
          Exibição das redes no vídeo
        </span>
        <i className={`fa-solid ${aberto ? "fa-chevron-up" : "fa-chevron-down"}`} style={{ color: "#5C4A63" }} />
      </button>

      {aberto && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <label style={labelStyle}>
            <input
              type="checkbox"
              checked={exibirRodape}
              onChange={(e) => toggleRodape(e.target.checked)}
              disabled={salvando}
              style={{ accentColor: "#73207C", width: 16, height: 16 }}
            />
            Exibir redes sociais no rodapé (durante os produtos)
          </label>

          <label style={labelStyle}>
            <input
              type="checkbox"
              checked={exibirOutro}
              onChange={(e) => toggleOutro(e.target.checked)}
              disabled={salvando}
              style={{ accentColor: "#73207C", width: 16, height: 16 }}
            />
            Exibir redes sociais no fechamento
          </label>

          {msg && (
            <div
              style={{
                fontSize: 12,
                color: msg.tipo === "ok" ? "#16a34a" : "#dc2626",
                marginTop: 4,
              }}
            >
              {msg.txt}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
